import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Stack,
    Divider,
    Grid,
    Avatar,
    Card,
    CardContent,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
} from "@mui/material";
import {
    Close as CloseIcon,
    Person as PersonIcon,
    LocalShipping as LocalShippingIcon,
    Payment as PaymentIcon,
    Schedule as ScheduleIcon,
    LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import {
    useGetAdminOrderDetailsQuery,
    useGetOrderPaymentsQuery,
    useGetOrderRemindersQuery,
} from "../../../store/orders/ordersApis";
import { baseUrl } from "../../../helpers/constants";
import { handleDateTimeDefault } from "../../../helpers/utils";

const OrderDetailsDialog = ({ open, onClose, orderId }) => {
    const [activeTab, setActiveTab] = useState(0);

    const {
        data: orderDetails,
        isLoading,
        error,
    } = useGetAdminOrderDetailsQuery(orderId, {
        skip: !orderId || !open,
    });

    // Debug logging
    console.log("OrderDetailsDialog - API call:", {
        orderId,
        open,
        isLoading,
        error,
        orderDetails,
    });

    const { data: payments } = useGetOrderPaymentsQuery(orderId, {
        skip: !orderId || !open,
    });

    const { data: reminders } = useGetOrderRemindersQuery(orderId, {
        skip: !orderId || !open,
    });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const getStatusColor = (status) => {
        const statusColors = {
            pending: { bg: "#fff3cd", color: "#856404" },
            initiated: { bg: "#cce5ff", color: "#004085" },
            partial: { bg: "#ffeaa7", color: "#6c5ce7" },
            paid: { bg: "#d4edda", color: "#155724" },
            failed: { bg: "#f8d7da", color: "#721c24" },
            cancelled: { bg: "#f5c6cb", color: "#721c24" },
            refunded: { bg: "#e2e3e5", color: "#383d41" },
        };
        return statusColors[status] || { bg: "#f8f9fa", color: "#6c757d" };
    };

    const getPaymentStatusColor = (status) => {
        const statusColors = {
            pending: { bg: "#fff3cd", color: "#856404" },
            completed: { bg: "#d4edda", color: "#155724" },
            failed: { bg: "#f8d7da", color: "#721c24" },
            refunded: { bg: "#e2e3e5", color: "#383d41" },
        };
        return statusColors[status] || { bg: "#f8f9fa", color: "#6c757d" };
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") {
            return "0.00";
        }
        const num = Number(price);
        if (isNaN(num)) {
            return "0.00";
        }
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    if (isLoading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                        <Typography>Loading order details...</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                        <Typography color="error">
                            Error loading order details: {error.message || "Unknown error"}
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (!orderDetails) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                        <Typography>No order details found</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    const order = orderDetails;

    // Debug logging
    console.log("OrderDetailsDialog - orderDetails:", orderDetails);
    console.log("OrderDetailsDialog - order:", order);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="h6">Order Details</Typography>
                        <Chip
                            label={order.order_number}
                            size="small"
                            sx={{
                                backgroundColor: "#e3f2fd",
                                color: "#1976d2",
                                fontWeight: 600,
                            }}
                        />
                        <Chip
                            label={order.payment_status?.toUpperCase() || "N/A"}
                            size="small"
                            sx={{
                                backgroundColor: getStatusColor(order.payment_status).bg,
                                color: getStatusColor(order.payment_status).color,
                                fontWeight: 600,
                            }}
                        />
                    </Stack>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab label="Order Information" />
                        <Tab label="Customer Details" />
                        <Tab label="Payment History" />
                        <Tab label="Reminders" />
                    </Tabs>
                </Box>

                {/* Order Information Tab */}
                {activeTab === 0 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                                    >
                                        <LocalShippingIcon />
                                        Order Information
                                    </Typography>
                                    <Stack spacing={2}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Order Number:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.order_number}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Total Amount:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#2e7d32" }}>
                                                ₹{formatPrice(order.total_amount)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Paid Amount:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#1976d2" }}>
                                                ₹{formatPrice(order.paid_amount)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Remaining Amount:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#d32f2f" }}>
                                                ₹{formatPrice(order.remaining_amount)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Payment Type:
                                            </Typography>
                                            <Chip
                                                label={order.payment_type?.toUpperCase() || "N/A"}
                                                size="small"
                                                sx={{
                                                    backgroundColor:
                                                        order.payment_type === "full" ? "#d1ecf1" : "#f8d7da",
                                                    color: order.payment_type === "full" ? "#0c5460" : "#721c24",
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </Stack>
                                        {order.next_payment_date && (
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="body2" color="text.secondary">
                                                    Next Payment Date:
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: "#ff9800" }}>
                                                    {new Date(order.next_payment_date).toLocaleDateString()}
                                                </Typography>
                                            </Stack>
                                        )}
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Created Date:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {handleDateTimeDefault(order.created_at)}
                                            </Typography>
                                        </Stack>
                                        {order.notes && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Notes:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        p: 2,
                                                        bgcolor: "#f8f9fa",
                                                        borderRadius: 1,
                                                        whiteSpace: "pre-wrap",
                                                    }}
                                                >
                                                    {order.notes}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                                    >
                                        <LocalShippingIcon />
                                        Product Information
                                    </Typography>
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        {order.product?.image_url && (
                                            <Avatar
                                                src={baseUrl + order.product.image_url}
                                                alt={order.product.name}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    border: "2px solid #e0e0e0",
                                                }}
                                            />
                                        )}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6" sx={{ mb: 1 }}>
                                                {order.product?.name || "N/A"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {order.product?.description || "No description available"}
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                                                ₹{formatPrice(order.product?.price)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Customer Details Tab */}
                {activeTab === 1 && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                <PersonIcon />
                                Customer Information
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={2}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Name:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.customer?.name || "N/A"}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Mobile:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.customer?.mobile || "N/A"}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Email:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.customer?.email || "N/A"}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">
                                                Region:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.customer?.region?.name || "N/A"}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Address:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                p: 2,
                                                bgcolor: "#f8f9fa",
                                                borderRadius: 1,
                                                whiteSpace: "pre-wrap",
                                            }}
                                        >
                                            {order.customer?.address || "No address provided"}
                                            {order.customer?.city &&
                                                `\n${order.customer.city}, ${order.customer.state}`}
                                            {order.customer?.pincode && ` - ${order.customer.pincode}`}
                                            {order.customer?.landmark && `\nLandmark: ${order.customer.landmark}`}
                                        </Typography>
                                        {order.customer?.notes && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Customer Notes:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        p: 2,
                                                        bgcolor: "#f8f9fa",
                                                        borderRadius: 1,
                                                        whiteSpace: "pre-wrap",
                                                    }}
                                                >
                                                    {order.customer.notes}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Payment History Tab */}
                {activeTab === 2 && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                <PaymentIcon />
                                Payment History
                            </Typography>
                            {payments && Array.isArray(payments) && payments.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Transaction ID</TableCell>
                                                <TableCell>Amount</TableCell>
                                                <TableCell>Method</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {payments.map((payment, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                                            {payment.transaction_id || "N/A"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 600, color: "#2e7d32" }}
                                                        >
                                                            ₹{formatPrice(payment.amount)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {payment.payment_method || "N/A"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={payment.status?.toUpperCase() || "N/A"}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getPaymentStatusColor(payment.status)
                                                                    .bg,
                                                                color: getPaymentStatusColor(payment.status).color,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {handleDateTimeDefault(payment.created_at)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No payment history available
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Reminders Tab */}
                {activeTab === 3 && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                <ScheduleIcon />
                                Reminders
                            </Typography>
                            {reminders && Array.isArray(reminders) && reminders.length > 0 ? (
                                <Stack spacing={2}>
                                    {reminders.map((reminder, index) => (
                                        <Card key={index} variant="outlined">
                                            <CardContent>
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="flex-start"
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                            {reminder.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ mb: 1 }}
                                                        >
                                                            {reminder.description}
                                                        </Typography>
                                                        <Stack direction="row" spacing={2}>
                                                            <Chip
                                                                label={reminder.type?.toUpperCase() || "CUSTOM"}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: "#e3f2fd",
                                                                    color: "#1976d2",
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                            <Chip
                                                                label={reminder.priority?.toUpperCase() || "MEDIUM"}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor:
                                                                        reminder.priority === "high"
                                                                            ? "#ffebee"
                                                                            : reminder.priority === "medium"
                                                                            ? "#fff3e0"
                                                                            : "#e8f5e8",
                                                                    color:
                                                                        reminder.priority === "high"
                                                                            ? "#d32f2f"
                                                                            : reminder.priority === "medium"
                                                                            ? "#f57c00"
                                                                            : "#2e7d32",
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                            <Chip
                                                                label={reminder.status?.toUpperCase() || "PENDING"}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor:
                                                                        reminder.status === "completed"
                                                                            ? "#d4edda"
                                                                            : reminder.status === "cancelled"
                                                                            ? "#f8d7da"
                                                                            : "#fff3cd",
                                                                    color:
                                                                        reminder.status === "completed"
                                                                            ? "#155724"
                                                                            : reminder.status === "cancelled"
                                                                            ? "#721c24"
                                                                            : "#856404",
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Box>
                                                    <Box sx={{ textAlign: "right" }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {new Date(reminder.reminder_date).toLocaleDateString()}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {reminder.reminder_time}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No reminders available
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderDetailsDialog;
